import {JsonObject} from "../common/json/json-object";
import {JsonProperty} from "../common/json/json-property";
import {v4 as uuid} from "uuid";
import {PathProps} from "../index";
import {BaseListItem, BaseListItemsLoader} from "../shared/types";
import {JSON_OBJECT} from "../shared/json/helpers";
import {getMemberAuth} from "../shared/auth";
import {Member, Members, User} from "../shared/entities";
import {DateUtil, WEEK_IN_MILLIS} from "../shared/date_util";
import {md5_uuid} from "../shared/md5";
import {GameData} from "./games/types";

export const ROOM_CODE_LENGTH = 6;

@JsonObject()
export class Settings {

  clone(): Settings {
    return JSON_OBJECT.deserializeObject(JSON_OBJECT.serializeObject(this), Settings);
  }
}

@JsonObject()
export class Layout {

  static createNew(creatorMark: string): Layout {
    return new Layout(md5_uuid(), creatorMark);
  }

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly creatorMark: string;

  constructor(id: string, creatorMark: string) {
    this.id = id;
    this.creatorMark = creatorMark;
  }
}


@JsonObject()
export class Room extends BaseListItem {

  joiner: Member;

  @JsonProperty()
  joinedBy: string;

  @JsonProperty()
  joinedAt: number;

  @JsonProperty()
  layout: Layout;

  @JsonProperty()
  completedAt: number;

  @JsonProperty()
  abandonedAt: number;

  @JsonProperty()
  settings: Settings = new Settings();

  constructor(id: string, creator: string, created: number) {
    super(id, creator, created);
  }

  amIPlayingWhite(): boolean {
    return (this.creator === getMemberAuth().getMemberId() && this.layout.creatorMark === "w")
      || (this.creator !== getMemberAuth().getMemberId() && this.layout.creatorMark === "b");
  }

  updateSelf(layout?: Layout, settings?: Settings): Room {
    if (layout) {
      this.layout = layout;
    }
    if (settings) {
      this.settings = settings;
    }
    this.completedAt = undefined;
    this.abandonedAt = undefined;
    return this;
  }

  async onAfterItemDeserialized(): Promise<void> {
    if (this.joinedBy) {
      this.joiner = await Members.getInstance().getOrLoadMember(this.joinedBy);
    }
  }

  isCompleted(): boolean {
    return this.completedAt > 0;
  }

  isPlayable(): boolean {
    return Date.now() < this.created + WEEK_IN_MILLIS && Boolean(this.joinedAt) && Boolean(this.joinedBy) && !this.completedAt && !this.abandonedAt;
  }
}

export class Rooms extends BaseListItemsLoader<Room> {

  private static instance: Rooms;

  static getInstance(): Rooms {
    if (!this.instance) {
      this.instance = new Rooms();
    }
    return this.instance;
  }

  constructor() {
    super({shared: true});
  }

  protected basePath(): string {
    return "rooms";
  }

  async loadListItems(): Promise<void> {
    throw new Error("Not supported. We should never try to list all rooms.")
  }

  protected deserializeItem(value: any): Room {
    return JSON_OBJECT.deserializeObject(value, Room);
  }

  protected serializeItem(item: Room): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Room, item2: Room): number {
    return 0;
  }
}

@JsonObject()
export class RoomJoin extends BaseListItem {

  static createNew(roomId: string): RoomJoin {
    return new RoomJoin(roomId, getMemberAuth().getMemberId(), Date.now());
  }

  constructor(id: string, creator: string, created: number) {
    super(id, creator, created);
  }
}

export class RoomJoins extends BaseListItemsLoader<RoomJoin> {

  private static instance: RoomJoins;

  static getInstance(): RoomJoins {
    if (!this.instance) {
      this.instance = new RoomJoins();
    }
    return this.instance;
  }

  protected basePath(): string {
    return "room_joins";
  }

  protected deserializeItem(value: any): RoomJoin {
    return JSON_OBJECT.deserializeObject(value, RoomJoin);
  }

  protected serializeItem(item: RoomJoin): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: RoomJoin, item2: RoomJoin): number {
    return item2.created - item1.created;
  }
}

@JsonObject()
export class RoomGame<GD extends GameData> extends BaseListItem {

  @JsonProperty()
  data: GD;

  static createNew<GD extends GameData>(roomId: string, data: GD): RoomGame<GD> {
    return new RoomGame(roomId, getMemberAuth().getMemberId(), Date.now(), data);
  }

  constructor(id: string, creator: string, created: number, data: GD) {
    super(id, creator, created);
    this.data = data;
  }
}

export class RoomGames<GD extends GameData> extends BaseListItemsLoader<RoomGame<GD>> {

  //@ts-ignore
  private static instance: RoomGame;

  static getInstance<GD extends GameData>(): RoomGames<GD> {
    if (!this.instance) {
      this.instance = new RoomGames<GD>();
    }
    return this.instance;
  }

  constructor() {
    super({shared: true});
  }

  protected basePath(): string {
    return "room_games";
  }

  protected deserializeItem(value: any): RoomGame<GD> {
    return JSON_OBJECT.deserializeObject(value, RoomGame<GD>);
  }

  protected serializeItem(item: RoomGame<GD>): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: RoomGame<GD>, item2: RoomGame<GD>): number {
    return item2.created - item1.created;
  }
}

@JsonObject()
export class Score extends BaseListItem {

  static createNew(gameId: string, level: string, points: number, duration: number) {
    const memberId = getMemberAuth().getMemberId();
    const now = new Date();
    return new Score(uuid(), memberId, now.getTime(), gameId, level, DateUtil.toDatestamp(now), points, duration);
  }

  @JsonObject()
  readonly scoreId;
  @JsonProperty()
  readonly gameId: string;
  @JsonProperty()
  readonly level: string;
  @JsonProperty()
  readonly ds: string;
  @JsonProperty()
  readonly points: number;
  @JsonProperty()
  readonly duration: number;

  constructor(scoreId: string, creator: string, created: number, gameId: string, level: string, ds: string, points: number, duration: number) {
    super(scoreId, creator, created);
    this.scoreId = scoreId;
    this.gameId = gameId;
    this.level = level;
    this.ds = ds;
    this.points = points;
    this.duration = duration;
  }
}

export function ScoreToString(score: Score) {
  return ScoreTimeToString(score.created);
}

export function ScoreTimeToString(time: number) {
  return new Date(time).toLocaleString("en-us", {
    dateStyle: "medium",
    timeStyle: "short"
  })
}

export class Scores extends BaseListItemsLoader<Score> {

  private static instance;

  static getInstance(): Scores {
    if (!this.instance) {
      this.instance = new Scores();
    }
    return this.instance;
  }

  protected basePath(): string {
    return "scores";
  }

  protected deserializeItem(value: any): Score {
    return JSON_OBJECT.deserializeObject(value, Score);
  }

  protected serializeItem(item: Score): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Score, item2: Score): number {
    // Reverse chronological
    return item2.created - item1.created;
  }
}

export type ScoreItem = {
  user: User,
  score: Score,
}

export class ScoreItems {

  readonly list: ScoreItem[] = [];

  add(user: User, score: Score) {
    this.list.push({user: user, score: score});
  }
}

export type Game = {
  id: string,
  name: string,
  description: string,
  icon: string,
  color: string,
  show: (pathProps: PathProps) => void,
}
